"use client"

import { Button } from "@/components/ui/button"
import { StoreCart, StoreProduct, StoreProductVariant } from "@/types/store"
import { addToCart } from "@lib/data/cart"
import { getProductsList } from "@lib/data/products"
import { useToggleState } from "@medusajs/ui"
import Link from "next/link"
import { normalizeUnitType } from "@/lib/util/normalize-unit"
import AddToCartModal from "@modules/products/components/add-to-cart-modal"
import GoToCartModal from "@modules/products/components/go-to-cart-modal"
import CalculateModal from "@modules/products/components/calculate-modal"
import VariantSelectPanel from "@modules/products/components/variant-select-panel"
import { AlertCircle, CreditCard, HelpCircle, ShoppingCart } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"

interface ProductSelectProps {
  product: StoreProduct
  selectedVariant: StoreProductVariant | undefined
  setSelectedVariant: (variant: StoreProductVariant) => void
  cart?: StoreCart | null
}

function ProductSelect({
  product,
  selectedVariant,
  setSelectedVariant,
  cart,
}: ProductSelectProps) {
  const { t } = useTranslation()

  const [similarProducts, setSimilarProducts] = useState<any[]>([])
  const {
    state: showAddToCartModal,
    open: openAddToCartModal,
    close: closeAddToCartModal,
  } = useToggleState(false)

  const {
    state: showGoToCartModal,
    open: openGoToCartModal,
    close: closeGoToCartModal,
  } = useToggleState(false)

  const [isAdding, setIsAdding] = useState(false)
  const [showCalculateModal, setShowCalculateModal] = useState(false)

  useEffect(() => {
    if (!selectedVariant && product.variants && product.variants.length > 0) {
      setSelectedVariant(product.variants[0])
    }
  }, [selectedVariant, product.variants, setSelectedVariant])

  const noInventory =
    selectedVariant && selectedVariant.stock_type === "infinite"

  const inStock = useMemo(() => {
    // If we don't manage inventory, we can always add to cart
    if (noInventory) {
      return true
    }

    // If we allow back orders on the variant, we can add to cart
    if (selectedVariant?.allow_backorder) {
      return true
    }

    if ((selectedVariant?.inventory_quantity || 0) > 0) {
      return true
    }

    // Otherwise, we can't add to cart
    return false
  }, [selectedVariant, noInventory])

  // Compute how many units of the selected variant are already in the cart
  const currentInCart = useMemo(() => {
    if (!cart || !selectedVariant?.id) return 0
    try {
      return (cart.items || []).reduce((sum: number, li: any) => {
        const liVariantId = li?.variant?.id
        const liQty = li?.variant?.quantity ?? 0
        return liVariantId === selectedVariant.id ? sum + liQty : sum
      }, 0)
    } catch {
      return 0
    }
  }, [cart, selectedVariant?.id])

  const isInfinite = noInventory || !!selectedVariant?.allow_backorder
  const inventoryQty = selectedVariant?.inventory_quantity ?? 0
  // Only when stock_type is "infinite" do we ignore stocks count for capping. Otherwise we cap by stocks length.
  const effectiveInventory = useMemo(() => {
    if (noInventory) return Number.POSITIVE_INFINITY
    const stocks = selectedVariant?.stocks
    if (stocks?.length) return Math.min(inventoryQty, stocks.length)
    return inventoryQty
  }, [noInventory, selectedVariant?.stocks, inventoryQty])
  // Cap quantity at available stock unless variant has stock_type "infinite" (not for allow_backorder)
  const remaining = useMemo(() => {
    if (noInventory) return Number.POSITIVE_INFINITY
    return Math.max(0, effectiveInventory - currentInCart)
  }, [noInventory, effectiveInventory, currentInCart])
  const showPurchasingControls = isInfinite || remaining > 0

  useEffect(() => {
    if (!selectedVariant) return
  }, [
    selectedVariant,
    inventoryQty,
    currentInCart,
    effectiveInventory,
    remaining,
    noInventory,
    isInfinite,
  ])

  const hasPartialPayment = useMemo(() => {
    if (!cart) return false
    return (cart.walletPaymentAmount ?? 0) > 0
  }, [cart])

  useEffect(() => {
    let isMounted = true
    async function fetchData() {
      // Build queryParams
      const queryParams: any = {}
      if (product.collection_id) {
        queryParams.collection_id = [product.collection_id]
      }
      if (product.tags) {
        queryParams.tag_id = product.tags
          .map((t: any) => t.id)
          .filter(Boolean) as string[]
      }
      queryParams.is_giftcard = false
      // Fetch similar products
      const { response } = await getProductsList({
        queryParams,
      })
      if (!isMounted) return
      setSimilarProducts(
        response.products.filter(
          (responseProduct: any) => responseProduct.id !== product.id
        )
      )
    }
    fetchData()
    return () => {
      isMounted = false
    }
  }, [product])

  const [quantity, setQuantity] = useState(inStock ? 1 : 0)
  const [lastAddedQty, setLastAddedQty] = useState<number | null>(null)

  useEffect(() => {
    if (noInventory) return
    if (quantity > remaining) {
      setQuantity(remaining)
    }
  }, [noInventory, quantity, remaining])

  const handleAddToCart = async (answers: { [key: string]: any }) => {
    if (!selectedVariant?.id) return null
    setIsAdding(true)
    const qtyToAdd = noInventory ? quantity : Math.min(quantity, remaining)
    if (!isInfinite && remaining <= 0) {
      setIsAdding(false)
      toast.error(t("product.noStockAvailable"))
      return null
    }
    try {
      await addToCart({
        variantId: selectedVariant.id,
        quantity: qtyToAdd,
        formAnswers: answers,
      })
      setIsAdding(false)
      setLastAddedQty(qtyToAdd)
      closeAddToCartModal()
      toast.success(t("product.addedToCart"))
    } catch (error: any) {
      setIsAdding(false)
      const errorMessage = error?.message || t("product.addToCartFailed")
      toast.error(errorMessage)
    }
  }

  const handleIncreaseQuantity = () => {
    if (!inStock) return
    if (!selectedVariant) return

    if (noInventory) {
      setQuantity((q) => q + 1)
      return
    }

    if (quantity < remaining) {
      setQuantity((q) => q + 1)
    }
  }

  return (
    <div className="flex h-full flex-col space-y-4 md:space-y-0">
      <div className="rounded-2xl border border-orange-100 bg-gradient-to-r from-orange-50/60 via-white to-orange-50/60 p-4">
        {selectedVariant?.sales_price &&
          selectedVariant?.calculated_price?.calculated_amount &&
          selectedVariant.sales_price >
            selectedVariant.calculated_price.calculated_amount && (
            <div className="flex flex-row items-center justify-start gap-2">
              <div className="flex flex-row rounded-full bg-orange-500 px-2 py-0.5 text-[11px] font-bold text-white">
                <div>
                  {`${Math.round(
                    ((selectedVariant.sales_price -
                      selectedVariant.calculated_price.calculated_amount) /
                      selectedVariant.sales_price) *
                      100
                  )}${t("product.off")}`}
                </div>
              </div>
              <div className="flex flex-row text-xl font-semibold text-slate-500">
                <div className="line-through">
                  {selectedVariant.sales_price?.toLocaleString("tr-TR", {
                    style: "currency",
                    currency: "TRY",
                  })}
                </div>
              </div>
            </div>
          )}
        <div className="flex flex-row items-center gap-4">
          <div className="text-4xl font-black tracking-tight text-slate-900">
            {(() => {
              // Infinite: use at least one stock for price (stocks[0]) when available; else variant price
              const stocks = selectedVariant?.stocks
              const stockForPrice = noInventory
                ? stocks?.[0]
                : stocks?.[quantity - 1]
              const price =
                typeof stockForPrice?.sales_price === "number"
                  ? stockForPrice.sales_price
                  : selectedVariant?.sales_price
              return typeof price === "number" ? (
                price.toLocaleString("tr-TR", {
                  style: "currency",
                  currency: "TRY",
                })
              ) : (
                <span className="text-lg font-semibold text-slate-500">
                  {t("product.productBeingSupplied")}
                </span>
              )
            })()}
          </div>
          {product.unit_price_type_name ? (
            <div className="flex h-8 w-fit items-center rounded-xl border border-orange-600 bg-orange-600 px-3 text-sm font-semibold text-white">
              {t(
                `cartDropdown.unitTypes.${normalizeUnitType(
                  product.unit_price_type_name
                )}` as any
              )}
            </div>
          ) : (
            <div className="flex h-8 w-fit items-center rounded-xl border border-orange-600 bg-orange-600 px-3 text-sm font-semibold text-white">
              {t("product.quantity")}
            </div>
          )}
        </div>
      </div>

      {/* Only show variant selection if not a single variant with banner */}
      {!(product.variants?.[0]?.show_category_products_banner === 1) && (
        <VariantSelectPanel
          product={product}
          selectedVariant={selectedVariant}
          setSelectedVariant={setSelectedVariant}
        />
      )}

      <div className="flex flex-grow flex-col">
        <div className="mt-auto flex items-center gap-2 pt-3 text-sm text-slate-600">
          <span className="inline-flex items-center gap-1">
            <HelpCircle className="mr-2 h-5 w-5 rounded-full text-orange-500" />
            {t("product.epinDeliveryInfo")}
          </span>
        </div>

        {!product.discountable && (
          <div className="mt-2 flex items-center gap-2 rounded-xl border border-yellow-200 bg-yellow-50 p-3">
            <AlertCircle className="w-4 h-4 text-yellow-600 flex-shrink-0" />
            <span className="text-sm text-yellow-800">
              {t("product.notEligibleForPromotion")}
            </span>
          </div>
        )}

        {/* Show warning if cart has partial payment */}
        {hasPartialPayment && (
          <div className="mb-4 flex items-center gap-2 rounded-xl border border-yellow-200 bg-yellow-50 p-2">
            <AlertCircle className="w-5 h-5 text-yellow-600" />
            <span className="text-sm text-yellow-800">
              {t("product.partialPaymentWarning")}
            </span>
          </div>
        )}

        <div className="mt-2 flex flex-wrap items-center gap-4">
          {showPurchasingControls && (
            <div className="flex items-center gap-2">
              <div
                className={`flex h-[42px] items-center overflow-hidden rounded-xl border border-orange-200 bg-white ${
                  !inStock ||
                  !selectedVariant ||
                  !selectedVariant?.sales_price ||
                  hasPartialPayment
                    ? "hidden"
                    : ""
                }`}
              >
                <button
                  disabled={!inStock || !selectedVariant || hasPartialPayment}
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className={`px-3 py-2 text-slate-700 ${
                    !inStock || !selectedVariant || hasPartialPayment
                      ? ""
                      : "hover:bg-orange-50"
                  }`}
                  aria-label={t("product.decreaseQuantity")}
                >
                  -
                </button>
                <input
                  type="number"
                  value={quantity}
                  disabled={!inStock || !selectedVariant || hasPartialPayment}
                  onChange={(e) => {
                    const value = parseInt(e.target.value)
                    if (!Number.isFinite(value)) return
                    const v = Math.max(1, value)
                    if (noInventory) return setQuantity(v)
                    setQuantity(Math.min(v, remaining))
                  }}
                  className={`w-16 border-x border-orange-200 bg-transparent text-center no-input-arrow`}
                  aria-label={t("product.quantityLabel")}
                />
                <button
                  onClick={handleIncreaseQuantity}
                  disabled={!inStock || !selectedVariant || hasPartialPayment}
                  className={`px-3 py-2 text-slate-700 ${
                    !inStock || !selectedVariant || hasPartialPayment
                      ? ""
                      : "hover:bg-orange-50"
                  }`}
                  aria-label={t("product.increaseQuantity")}
                >
                  +
                </button>
              </div>
            </div>
          )}

          {!isInfinite && remaining === 0 && (
            <div className="text-xs text-muted-foreground mt-1">
              {t("product.maxQuantityReached")}
            </div>
          )}

          {hasPartialPayment ? (
            <Link
              href="/cart"
              className="flex flex-1 items-center justify-center rounded-xl border border-orange-200 bg-orange-50 px-5 py-2 text-orange-700 hover:bg-orange-100"
            >
              <CreditCard className="w-5 h-5 mr-2" />
              {t("product.completeCurrentOrder")}
            </Link>
          ) : showPurchasingControls ? (
            <div className="flex flex-col gap-2 w-full">
              <div className="flex gap-2">
                <Button
                  data-testid="add-product-button"
                  onClick={openAddToCartModal}
                  disabled={
                    !inStock ||
                    !selectedVariant ||
                    !selectedVariant?.sales_price ||
                    typeof selectedVariant?.sales_price !== "number" ||
                    hasPartialPayment
                  }
                  className="flex-1 rounded-xl border border-orange-200 bg-white px-5 py-2.5 font-semibold text-slate-700 hover:bg-orange-50 hover:text-orange-700"
                >
                  <ShoppingCart className="w-5 h-5 mr-2" />
                  {!selectedVariant
                    ? t("product.selectVariant")
                    : !inStock
                    ? t("product.productBeingSupplied")
                    : t("product.addToCart")}
                </Button>
                {product.include_in_calculation_helper === 1 && (
                  <Button
                    onClick={() => setShowCalculateModal(true)}
                    className="rounded-xl border border-orange-600 bg-orange-600 px-5 py-2.5 font-semibold text-white hover:bg-orange-700"
                  >
                    {t("product.calculate")}
                  </Button>
                )}
              </div>
              {selectedVariant && (
                <div className="flex flex-wrap gap-2 text-sm text-slate-600 mt-2">
                  {selectedVariant.width && <span>{t("product.width")}: {selectedVariant.width}cm</span>}
                  {selectedVariant.length && <span>{t("product.length")}: {selectedVariant.length}cm</span>}
                  {selectedVariant.height && <span>{t("product.height")}: {selectedVariant.height}cm</span>}
                  {selectedVariant.thickness && <span>{t("product.thickness")}: {selectedVariant.thickness}cm</span>}
                </div>
              )}
            </div>
          ) : null}

          {/* check if customer then show this btn */}
          {/* {customer && (
            <Button
              //   isLoading={isAdding}
              onClick={goToCheckout}
              disabled={!inStock || !selectedVariant}
              className="flex-1 border-2 border-muted-accent bg-button-primary hover:bg-button-primary-lighten text-slate-900 px-5 py-2.5"
            >
              <HandCoins className="w-5 h-5 mr-2" />
              {!selectedVariant
                ? "Select variant"
                : !inStock
                ? "Out of stock"
                : "Buy now"}
            </Button>
          )} */}
        </div>
        {selectedVariant && !hasPartialPayment && (
          <AddToCartModal
            isOpen={showAddToCartModal}
            closeModal={closeAddToCartModal}
            handleAddToCart={handleAddToCart}
            isAdding={isAdding}
            variant={selectedVariant}
            quantity={quantity}
            setQuantity={setQuantity}
            selectedVariant={selectedVariant}
            productUnitTypeName={product.unit_price_type_name}
            maxQuantity={Number.isFinite(remaining) ? remaining : undefined}
          />
        )}
        {selectedVariant && !hasPartialPayment && (
          <GoToCartModal
            similarProducts={similarProducts}
            isOpen={showGoToCartModal}
            closeModal={closeGoToCartModal}
            product={product}
            selectedVariant={selectedVariant}
            variant={selectedVariant}
            quantity={quantity}
            addedQuantity={lastAddedQty ?? undefined}
          />
        )}
        {selectedVariant && (
          <CalculateModal
            isOpen={showCalculateModal}
            closeModal={() => setShowCalculateModal(false)}
            variant={selectedVariant}
            unitPriceType={product.unit_price_type_name}
          />
        )}
      </div>
    </div>
  )
}

export default ProductSelect
